import { useRef, useState } from 'react';

import { api, type PhoneNumber } from './api.js';

interface Props {
  number: PhoneNumber;
  onDone: () => void;
  onCancel: () => void;
}

type Slot = 'greeting' | 'voicemail';

/**
 * Greeting audio for one number.
 *
 * Two slots: the message callers hear while the phone rings, and the message
 * before voicemail records. Both are stored unencrypted on the volume
 * FreeSWITCH reads — they are announcements played to every caller, and
 * FreeSWITCH cannot decrypt a file mid-call.
 */
export function NumberGreetings({ number, onDone, onCancel }: Props): JSX.Element {
  const [busy, setBusy] = useState<Slot | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [current, setCurrent] = useState({
    greeting: number.greetingName,
    voicemail: number.voicemailGreetingName,
  });

  const inputs = {
    greeting: useRef<HTMLInputElement>(null),
    voicemail: useRef<HTMLInputElement>(null),
  };

  async function upload(slot: Slot, file: File): Promise<void> {
    setBusy(slot);
    setError(null);
    try {
      const result = await api.uploadGreeting(number.id, slot, file);
      setCurrent((c) => ({ ...c, [slot]: result.name }));
      onDone();
    } catch (err) {
      setError(err instanceof Error ? err.message : String(err));
    } finally {
      setBusy(null);
    }
  }

  async function remove(slot: Slot): Promise<void> {
    setBusy(slot);
    try {
      await api.removeGreeting(number.id, slot);
      setCurrent((c) => ({ ...c, [slot]: null }));
      onDone();
    } catch (err) {
      setError(err instanceof Error ? err.message : String(err));
    } finally {
      setBusy(null);
    }
  }

  function slotRow(slot: Slot, title: string, description: string): JSX.Element {
    const name = current[slot];
    return (
      <div className="greeting-slot">
        <div className="greeting-meta">
          <strong>{title}</strong>
          <span>{description}</span>
          {name ? (
            <span className="greeting-file">Current: {name}</span>
          ) : (
            <span className="greeting-file none">No audio uploaded</span>
          )}
        </div>

        <div className="greeting-actions">
          <input
            ref={inputs[slot]}
            type="file"
            accept="audio/wav,audio/mpeg,.wav,.mp3"
            style={{ display: 'none' }}
            onChange={(e) => {
              const file = e.target.files?.[0];
              if (file) void upload(slot, file);
              e.target.value = '';
            }}
          />
          <button
            type="button"
            className="secondary"
            disabled={busy !== null}
            onClick={() => inputs[slot].current?.click()}
          >
            {busy === slot ? 'Uploading…' : name ? 'Replace' : 'Upload'}
          </button>
          {name && (
            <button
              type="button"
              className="link danger"
              disabled={busy !== null}
              onClick={() => void remove(slot)}
            >
              Remove
            </button>
          )}
        </div>
      </div>
    );
  }

  return (
    <div className="card form">
      <button type="button" className="link" onClick={onCancel}>
        ← Numbers
      </button>

      <h2>Audio for {number.e164}</h2>

      {slotRow(
        'greeting',
        'Ring greeting',
        'Answers instantly and plays to the caller while the phone rings. Keep it short — an agent can pick up part-way through.',
      )}

      {slotRow(
        'voicemail',
        'Voicemail greeting',
        'Played before recording starts when nobody answers. Falls back to a default message if none is set.',
      )}

      <p className="hint">
        WAV or MP3, up to 5 MB. WAV at 8 kHz or 16 kHz mono plays without
        transcoding and sounds cleanest over a phone line.
      </p>

      {error && <p className="error">{error}</p>}
    </div>
  );
}
